import React from 'react'
import { Box, type SxProps, type Theme } from '@mui/material'

export type ScorePaletteEntry = string | { background: string; color?: string }
export type ScorePalette = Record<number, ScorePaletteEntry>
export type ScoreBadgeSize = 'dense' | 'compact' | 'regular' | 'large'

export type BaseScoreBadgeProps = {
  score?: number | null
  labels: Record<number, string>
  palette?: ScorePalette
  size?: ScoreBadgeSize
  min?: number
  max?: number
  fallback?: React.ReactNode
  sx?: SxProps<Theme>
}

export type HazardScoreBadgeProps = {
  score?: number | null
  size?: ScoreBadgeSize
  palette?: ScorePalette
  labelsOverride?: Record<number, string>
  fallback?: React.ReactNode
  sx?: SxProps<Theme>
}

const SIZE_STYLE: Record<ScoreBadgeSize, { px: number; py: number; fontSize: number; minWidth: number }> = {
  dense: { px: 2, py: 0.8, fontSize: 11, minWidth: 64 },
  compact: { px: 6, py: 3, fontSize: 11, minWidth: 78 },
  regular: { px: 8, py: 4, fontSize: 12, minWidth: 92 },
  large: { px: 10, py: 5, fontSize: 13, minWidth: 108 },
}

export const DEFAULT_SCORE_PALETTE: ScorePalette = {
  1: { background: '#c6e8be', color: '#122612' },
  2: { background: '#f7e4af', color: '#362600' },
  3: { background: '#f6c8a4', color: '#3a1f00' },
  4: { background: '#f3a5a5', color: '#3a0c0c' },
  5: { background: '#e8747d', color: '#33060a' },
}

export function normalizeScore(score: number, min = 1, max = 5): number {
  if (Number.isNaN(score)) return min
  const rounded = Math.round(score)
  return Math.min(Math.max(rounded, min), max)
}

function pickTextColor(hexColor: string): string {
  const hex = hexColor.replace('#', '')
  if (hex.length !== 6) return '#000000'
  const r = parseInt(hex.substring(0, 2), 16)
  const g = parseInt(hex.substring(2, 4), 16)
  const b = parseInt(hex.substring(4, 6), 16)
  // YIQ contrast
  const yiq = (r * 299 + g * 587 + b * 114) / 1000
  return yiq >= 150 ? '#000000' : '#ffffff'
}

function resolveColors(score: number, palette?: ScorePalette): { background: string; color: string } | null {
  if (!palette) return null
  const entry = palette[score]
  console.log('resolveColors: entry ', entry)
  if (!entry) return null
  if (typeof entry === 'string') {
    return { background: entry, color: pickTextColor(entry) }
  }
  return {
    background: entry.background,
    color: entry.color ?? pickTextColor(entry.background),
  }
}

export function ScoreBadge({
  score,
  labels,
  palette = DEFAULT_SCORE_PALETTE,
  size = 'compact',
  min = 1,
  max = 5,
  fallback = '—',
  sx,
}: BaseScoreBadgeProps) {
  const normalized = typeof score === 'number' ? normalizeScore(score, min, max) : null
  const label = normalized != null ? labels[normalized] ?? `Score ${normalized}` : null
  const colors = normalized != null ? resolveColors(normalized, palette) : null
  const { px, py, fontSize, minWidth } = SIZE_STYLE[size]

  if (normalized == null) return <>{fallback}</>
  return (
    <Box
      component="span"
      sx={{
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        px,
        py,
        minWidth,
        borderRadius: 999,
        fontSize,
        fontWeight: 600,
        letterSpacing: 0.2,
        border: '1px solid rgba(15, 23, 42, 0.06)',
        boxShadow: '0 8px 18px rgba(15, 23, 42, 0.08)',
        backgroundColor: colors?.background ?? DEFAULT_SCORE_PALETTE[normalized]?.['background'],
        color: colors?.color ?? (DEFAULT_SCORE_PALETTE[normalized] as any)?.['color'] ?? '#000',
        lineHeight: 1.2,
        ...sx,
      }}>
      {label}
    </Box>
  )
}

export function createHazardScoreBadge(config: {
  labels: Record<number, string>
  palette?: ScorePalette
  min?: number
  max?: number
  mapScore?: (value: number | null | undefined) => number | null
}) {
  return function HazardScoreBadge({ score, size, palette, labelsOverride, fallback, sx }: HazardScoreBadgeProps) {
    const mapped = typeof config.mapScore === 'function' ? config.mapScore(score) : score
    return (
      <ScoreBadge
        score={mapped}
        size={size}
        min={config.min}
        max={config.max}
        labels={labelsOverride ?? config.labels}
        palette={palette ?? config.palette ?? DEFAULT_SCORE_PALETTE}
        fallback={fallback}
        sx={sx}
      />
    )
  }
}
